import { Icons } from '@cockpitai/components/atoms'
import { Typography } from '@cockpitai/components/atoms/Typography'
import { routesPath } from '@cockpitai/constants/routesPath'
import { useLevelNavigation } from '@cockpitai/store/navigation/useLevelNavigation'
import {
  useSideBarStore,
  type Favorite,
} from '@cockpitai/store/navigation/useSideBarStore'
import { goToScrollTop } from '@cockpitai/utils'
import { AnimatePresence, motion } from 'framer-motion'
import Lottie from 'lottie-react'
import { useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import { useSendFavorite } from '@cockpitai/integration/favorites/hooks'
import { sendAnalyticsEvent } from '@cockpitai/utils/analytics'
import { hideWord } from '@cockpitai/utils/hideWord'
import { urlDecipher } from '@cockpitai/utils/urlCipher'
import { useNavigationType } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import animationData from './startAnimation.json'

export function NavigationLevel() {
  const [stared, setStared] = useState<string>('')
  const navigationType = useNavigationType()

  const { mutateAsync } = useSendFavorite()

  const {
    navigationHistory,
    resetNavigation,
    removeNavigation,
    setInTransition,
  } = useLevelNavigation()

  const { addFavorite, favorites, updateFavoriteItem } = useSideBarStore()

  const handleTransition = () => {
    setInTransition(true)

    setTimeout(() => {
      setInTransition(false)
    }, 1000)
  }

  const navigate = useNavigate()

  const currentUrl = new URL(window.location.href)

  const affiliateId = currentUrl.searchParams.get('affiliateId')
  const occupationType = currentUrl.searchParams.get('occupationType')
  const withoutWallet = currentUrl.searchParams.get('withoutWallet')

  useEffect(() => {
    if (!affiliateId) resetNavigation()
  }, [affiliateId, resetNavigation])

  useEffect(() => {
    // Adiciona verificação para evitar redirecionamento desnecessário no carregamento inicial
    if (navigationType === 'POP' && navigationHistory.length > 0) {
      const itemIndex = navigationHistory.findIndex(
        (item) => {
          const params = new URLSearchParams(urlDecipher(item.data))
          const affiliateIdParam = params.get('affiliateId')
          const occupationTypeParam = params.get('occupationType')
          const withoutWalletParam = params.get('withoutWallet')

          return affiliateIdParam === affiliateId && occupationTypeParam === occupationType && withoutWalletParam === withoutWallet
        }
      )

      if (itemIndex !== -1) {
        removeNavigation(itemIndex)
      } else {
        resetNavigation()
        navigate(routesPath.dashboard.home, { replace: true })
      }
    }
  }, [navigationType, affiliateId, withoutWallet, occupationType, navigationHistory.length])

  const handleHistoryNavigation = (clearHistory: boolean, data: string) => {
    goToScrollTop()

    handleTransition()

    if (clearHistory) return resetNavigation()

    const itemIndex = navigationHistory.findIndex((item) => item.data === data)

    removeNavigation(itemIndex)
  }

  const handleAddToFav = (item: Favorite) => async () => {
    addFavorite(item)

    item?.data && setStared(item.data)

    try {
      const favorite = await mutateAsync({
        name: item.name,
        data: item.data,
      })

      updateFavoriteItem(favorite)

      sendAnalyticsEvent('hierarquia_favorite_added', {
        item_name: item.name
      })

    } catch (error) {
      console.error(error)
    }

    setTimeout(() => {
      setStared('')
    }, 1000)
  }

  useEffect(() => {
    if (navigationHistory.length > 0) {
      const breadcrumbTree = navigationHistory.map(item => item.name).join(' / ')
      sendAnalyticsEvent('hierarquia', { breadcrumb: breadcrumbTree })
    }

  }, [navigationHistory])

  return navigationHistory.length > 0 ? (
    <div className="level-navigation-menu">
      {navigationHistory.map((item, index) => {
        const alreadyFavorite = favorites.some((fav) => fav.data === item.data)

        return (
          <div
            key={`level-navigation-item-${item.data}`}
            className="level-navigation-item">
            {index > 0 && (
              <button
                className={twMerge(
                  'relative',
                  alreadyFavorite && 'pointer-events-none',
                )}
                onClick={handleAddToFav({
                  data: item.data,
                  name: item.name,
                  id: item.id,
                })}>
                <Icons.Star
                  width={14}
                  height={14}
                  className={alreadyFavorite ? 'text-others-orange' : ''}
                />

                <AnimatePresence>
                  {stared === item.data && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}>
                      <Lottie
                        style={{
                          position: 'absolute',
                          width: 24,
                          height: 24,
                          top: '-5px',
                          left: '-5px',
                        }}
                        animationData={animationData}
                        loop
                        autoPlay
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            )}
            {index < navigationHistory.length - 1 ? (
              <NavLink
                to={
                  item.data
                    ? `${routesPath.dashboard.home}${urlDecipher(item.data)}`
                    : routesPath.dashboard.home
                }
                onClick={() => handleHistoryNavigation(!item.data, item.data)}>
                <Typography className="whitespace-nowrap text-sm" font="outfit">
                  {hideWord('REDE ', item.name)}
                </Typography>
              </NavLink>
            ) : (
              <Typography className="whitespace-nowrap text-sm" font="outfit">
                {hideWord('REDE ', item.name)}
              </Typography>
            )}
            {index < navigationHistory.length - 1 && (
              <span className="pr-2 text-xs text-gray-400 lg:px-5">/</span>
            )}
          </div>
        )
      })}
    </div>
  ) : (
    <></>
  )
}
